import React from 'react';
import styled from 'styled-components';
import { Box, Flex } from 'reflexbox';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

import { Link } from '@treams/treams-routes';

import * as graphql from 'generated/graphql';
import { toRgbString } from 'packages/kakapo-ui-kit/helpers/HexColor';
import { ParagraphSmall } from 'packages/kakapo-ui-kit/components/common/Paragraph';
import StickToSide from 'packages/kakapo-ui-kit/components/atoms/StickToSide';
import useLocalize from 'packages/kakapo-ui-application/hooks/useLocalize';
import { useLocation } from 'react-router-dom';

const LearningEventCardImageContainer = styled.div`
	border-radius: 6px 6px 0 0;
	width: 100%;
	height: 150px;
	background: ${(props) => props.theme.colors.slateBlue};
	overflow: hidden;
`;

const LearningEventCardImage = styled.img<{ cancelled: boolean }>`
	width: 100%;
	height: 100%;
	object-fit: cover;
	display: block;
	${(props) => (props.cancelled ? 'opacity: 0.4;' : '')}
`;

const LearningEventCardElement = styled(Link)`
	box-shadow: 0 2px 4px 0 rgba(${(props) => toRgbString(props.theme.colors.slateBlue)}, 0.3);
	border-radius: 6px;
	display: block;
	transition: transform ease-out 250ms, box-shadow ease-out 250ms;
	position: relative;
	height: 100%;

	&:hover {
		transform: translateY(-3px);
		box-shadow: 0 2px 10px 0 rgba(${(props) => toRgbString(props.theme.colors.slateBlue)}, 0.5);
	}
`;

const LearningEventCardBox = styled(Box)`
	background-color: ${(props) => props.theme.colors.white};
	background: #ffffff;
	border-radius: 0 0 6px 6px;
`;

const LearningEventCardDot = styled(Box)<{ $color: string }>`
	background: ${(props) => props.$color};
	width: 10px;
	height: 10px;
	border-radius: 5px;
	margin-top: -3px;
	flex-shrink: 0;
	flex-grow: 0;
`;

const CalendarIconMonth = styled.p`
	color: ${(props) => props.theme.colors.white};
	background-color: ${(props) => props.theme.colors.danger};
	border-radius: 5px 5px 0 0;
	display: flex;
	font-size: 10px;
	align-items: center;
	justify-content: center;
	text-transform: uppercase;
	font-weight: 600;
	padding: 2px 5px 0 5px;
	line-height: 14px;
	font-smoothing: subpixel-antialiased;
`;
const CalendarIconDay = styled.p`
	color: ${(props) => props.theme.colors.antracite};
	background-color: ${(props) => props.theme.colors.white};
	display: flex;
	font-size: 16px;
	align-items: center;
	justify-content: center;
	border-radius: 0 0 5px 5px;
	padding: 2px 0;
`;

const CalendarIcon = ({ date }: { date: Date }) => {
	return (
		<Box width={36}>
			<CalendarIconMonth>{moment(date).format('MMM')}</CalendarIconMonth>
			<CalendarIconDay>{moment(date).date()}</CalendarIconDay>
		</Box>
	);
};

const LearningEventTitle = styled(ParagraphSmall)<{ cancelled: boolean }>`
	${(props) => (props.cancelled ? 'text-decoration: line-through;' : '')}
`;

const Pill = styled.div`
	background-color: ${(props) => props.theme.colors.red};
	border-radius: 5px;
	color: #ffffff;
	padding: 0 10px;
`;

const LearningEventCard = (props: { event: graphql.LearningEvent }) => {
	const { localize } = useLocalize();
	const { t } = useTranslation();
	const location = useLocation();

	const cancelled = props.event.status === graphql.LearningEventStatus.Cancelled;

	return (
		<LearningEventCardElement to={`/learning/event/${props.event.id}`} navigationState={{ backgroundLocation: location }}>
			<LearningEventCardImageContainer>
				{props.event.cover?.source.url && (
					<LearningEventCardImage
						src={props.event.cover?.source.url + '?d=300x150'}
						cancelled={cancelled}
					/>
				)}
			</LearningEventCardImageContainer>

			{cancelled && (
				<StickToSide top={10} left={10}>
					<Pill>{t('learning.event.cancelled')}</Pill>
				</StickToSide>
			)}

			<StickToSide top={10} right={10}>
				<CalendarIcon date={props.event.startDate} />
			</StickToSide>

			<LearningEventCardBox py={12} px={3}>
				<Flex alignItems="center">
					<Box flexGrow={1} flexShrink={1}>
						<LearningEventTitle color="slateBlue" cancelled={cancelled}>
							{localize(props.event.title)?.text}
						</LearningEventTitle>
					</Box>
					<LearningEventCardDot $color={props.event.learningType.color} />
				</Flex>
			</LearningEventCardBox>
		</LearningEventCardElement>
	);
};

export default LearningEventCard;
